import React, { useEffect, useState } from 'react';
import {
  consumeLiveDashboardTokenHandoff,
  fetchLiveDashboardSession,
  getStoredLiveAllowedModules,
  isLocalPermissionPreviewEnabled,
  liveDashboardLogin,
} from '../services/liveDashboardAuthService';
import { getAccessToken, getRefreshToken } from '../utils/storage';
import { isEmbedded, notifyParentSessionExpired } from '../utils/embed';

interface OnboardingGuardProps {
  children: React.ReactNode;
}

const OnboardingGuard: React.FC<OnboardingGuardProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    const checkLiveAccess = async () => {
      const hasTokenHandoff = consumeLiveDashboardTokenHandoff();

      // When tokens arrive via URL hash (Live Rage app handoff), exchange the original
      // Rage JWT for a short internal token via live-login. This avoids sending the
      // large Rage JWT in Authorization headers on subsequent /me calls, which Cloudflare blocks.
      if (hasTokenHandoff) {
        const accessToken = getAccessToken();
        const refreshToken = getRefreshToken();
        if (accessToken) {
          try {
            const result = await liveDashboardLogin({
              authToken: accessToken,
              refreshToken: refreshToken || undefined,
            });
            if ('allowedModules' in result) {
              // Full session saved with internal JWT — no need for a separate /me call
              setAccessDenied(false);
              setIsLoading(false);
              return;
            }
          } catch (error) {
            console.error('Token handoff exchange failed:', error);
            setAccessDenied(true);
            setIsLoading(false);
            return;
          }
        }
      }

      if (!hasTokenHandoff && isLocalPermissionPreviewEnabled() && getStoredLiveAllowedModules()?.length) {
        setAccessDenied(false);
        setIsLoading(false);
        return;
      }

      try {
        await fetchLiveDashboardSession();
        setAccessDenied(false);
      } catch (error) {
        console.error('Failed to validate live dashboard access:', error);
        // Give Rage a chance to re-handoff a fresh token before showing any
        // fallback UI - mirrors the same recovery signal used in api.ts /
        // LiveLoginPage for every other auth-failure path.
        notifyParentSessionExpired();
        setAccessDenied(true);
      } finally {
        setIsLoading(false);
      }
    };

    checkLiveAccess();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (accessDenied) {
    // Embedded end-users must never see the dev "Test live-login" link or
    // developer-facing copy - only a standalone/dev session gets that.
    if (isEmbedded()) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-6 dark:bg-gray-900">
          <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-8 text-center shadow-sm dark:border-slate-700 dark:bg-slate-800">
            <h1 className="text-lg font-bold text-slate-900 dark:text-slate-50">Your session has expired</h1>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-300">
              Please reload the page to continue using the dashboard.
            </p>
            <button
              className="mt-5 rounded-md bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700"
              onClick={() => window.location.reload()}
              type="button"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-6">
        <div className="max-w-md rounded-lg border border-slate-200 bg-white p-6 text-left shadow-sm dark:border-slate-700 dark:bg-slate-800">
          <div className="text-xs font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400 mb-2">
            AI Dashboard
          </div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-slate-50 mb-2">
            Live Rage access required
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Open the dashboard from the Live Rage application so your token can be validated.
          </p>
          <a
            className="mt-4 inline-flex rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            href="/live-login"
          >
            Test live-login
          </a>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default OnboardingGuard;
